/**
 * Create-template dialog for WhatsApp settings (#1755).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  DialogContent,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { useNotify } from '@/hooks/useNotify';
import { useAuthStore } from '@/stores/authStore';
import { MANAGE_WHATSAPP_PERMISSION } from '../permissions';
import { renderTemplateBody } from '@/utils/whatsappTemplate';
import {
  WHATSAPP_TEMPLATE_PARAMETER_SOURCES,
  isWhatsAppTemplateParameterSource,
  type WhatsAppTemplateParameterSource,
} from '@/lib/whatsappTemplateSources';
import {
  isTemplateValidationCode,
  isWhatsAppTemplateCategory,
  isWhatsAppTemplateLanguage,
  countTemplateParameters,
  readWhatsAppTemplateApiError,
  validateWhatsAppTemplate,
  TEMPLATE_BODY_MAX_LENGTH,
  TEMPLATE_FOOTER_MAX_LENGTH,
  TEMPLATE_NAME_MAX_LENGTH,
  WHATSAPP_TEMPLATE_CATEGORIES,
  WHATSAPP_TEMPLATE_LANGUAGES,
  type WhatsAppTemplateCategory,
  type WhatsAppTemplateLanguage,
} from './templateValidation';
import {
  useCreateWhatsAppTemplate,
  visibleMetaErrorText,
} from './useWhatsAppTemplateMutations';

/** Meta does not accept an unbounded parameter list; never allocate from the raw index. */
const MAX_TEMPLATE_PLACEHOLDER_INDEX = 10;

function isPlaceholderIndexTooHigh(body: string): boolean {
  const highest = countTemplateParameters(body);
  if (!Number.isSafeInteger(highest) || highest > MAX_TEMPLATE_PLACEHOLDER_INDEX) {
    return true;
  }
  // countTemplateParameters skips non-finite matches (very long digit runs).
  for (const match of body.matchAll(/\{\{\s*(\d+)\s*\}\}/g)) {
    const index = Number(match[1]);
    if (!Number.isSafeInteger(index) || index > MAX_TEMPLATE_PLACEHOLDER_INDEX) {
      return true;
    }
  }
  return false;
}

function exampleFieldCount(body: string): number {
  if (isPlaceholderIndexTooHigh(body)) return 0;
  const highest = countTemplateParameters(body);
  if (!Number.isSafeInteger(highest) || highest <= 0) return 0;
  return Math.min(highest, MAX_TEMPLATE_PLACEHOLDER_INDEX);
}

function resizeExamples(prev: readonly string[], nextCount: number): string[] {
  if (!Number.isSafeInteger(nextCount) || nextCount < 0 || nextCount > MAX_TEMPLATE_PLACEHOLDER_INDEX) {
    return [];
  }
  if (nextCount === prev.length) return prev.slice();
  if (nextCount < prev.length) return prev.slice(0, nextCount);
  return [...prev, ...Array.from({ length: nextCount - prev.length }, () => '')];
}

function resizeSources(
  prev: readonly (WhatsAppTemplateParameterSource | null)[],
  nextCount: number,
): (WhatsAppTemplateParameterSource | null)[] {
  if (!Number.isSafeInteger(nextCount) || nextCount < 0 || nextCount > MAX_TEMPLATE_PLACEHOLDER_INDEX) {
    return [];
  }
  if (nextCount === prev.length) return prev.slice();
  if (nextCount < prev.length) return prev.slice(0, nextCount);
  return [...prev, ...Array.from({ length: nextCount - prev.length }, () => null)];
}

const LANGUAGE_ENDONYMS: Record<WhatsAppTemplateLanguage, string> = {
  fi: 'Suomi',
  sv: 'Svenska',
  en: 'English',
};

interface WhatsAppTemplateCreateDialogProps {
  open: boolean;
  onClose: () => void;
}

export function WhatsAppTemplateCreateDialog({ open, onClose }: WhatsAppTemplateCreateDialogProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canManageWhatsApp = hasPermission(MANAGE_WHATSAPP_PERMISSION);
  const createTemplate = useCreateWhatsAppTemplate();

  const [name, setName] = useState('');
  const [language, setLanguage] = useState<WhatsAppTemplateLanguage>('fi');
  const [category, setCategory] = useState<WhatsAppTemplateCategory>('UTILITY');
  const [body, setBody] = useState('');
  const [footer, setFooter] = useState('');
  const [examples, setExamples] = useState<string[]>([]);
  const [sources, setSources] = useState<(WhatsAppTemplateParameterSource | null)[]>([]);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [metaErrorText, setMetaErrorText] = useState('');

  useEffect(() => {
    if (!open) return;
    const initialBody = '';
    setName('');
    setLanguage('fi');
    setCategory('UTILITY');
    setBody(initialBody);
    setFooter('');
    setExamples(resizeExamples([], exampleFieldCount(initialBody)));
    setSources(resizeSources([], exampleFieldCount(initialBody)));
    setErrorCode(null);
    setConflict(false);
    setMetaErrorText('');
  }, [open]);

  const isDirty =
    name !== '' ||
    body !== '' ||
    footer !== '' ||
    examples.some((value) => value !== '') ||
    sources.some((value) => value !== null) ||
    language !== 'fi' ||
    category !== 'UTILITY';

  const placeholderIndexTooHigh = isPlaceholderIndexTooHigh(body);
  const visibleExampleCount = exampleFieldCount(body);

  const handleBodyChange = (value: string) => {
    setBody(value);
    const nextCount = exampleFieldCount(value);
    setExamples((prev) => (nextCount === prev.length ? prev : resizeExamples(prev, nextCount)));
    setSources((prev) => (nextCount === prev.length ? prev : resizeSources(prev, nextCount)));
  };

  const handleSubmit = async () => {
    if (!hasPermission(MANAGE_WHATSAPP_PERMISSION)) return;
    setErrorCode(null);
    setConflict(false);
    setMetaErrorText('');

    if (isPlaceholderIndexTooHigh(body)) {
      return;
    }

    const code = validateWhatsAppTemplate({
      name,
      language,
      category,
      body,
      examples,
    });
    if (code) {
      setErrorCode(code);
      return;
    }

    const trimmedFooter = footer.trim();
    const parameterSources = Array.from(
      { length: visibleExampleCount },
      (_, index) => sources[index] ?? null,
    );
    const payload = {
      name: name.trim(),
      language,
      category,
      body: body.trim(),
      examples: examples.map((value) => value.trim()),
      ...(trimmedFooter ? { footer: trimmedFooter } : {}),
      ...(visibleExampleCount > 0 ? { parameterSources } : {}),
    };

    try {
      await createTemplate.mutateAsync(payload);
      notify.success(t('whatsapp.templateCreated'), t('whatsapp.templateCreatedDesc'));
      onClose();
    } catch (err) {
      const parsed = readWhatsAppTemplateApiError(err);
      if (parsed?.status === 400 && parsed.code && isTemplateValidationCode(parsed.code)) {
        setErrorCode(parsed.code);
        return;
      }
      if (parsed?.status === 409) {
        setConflict(true);
        return;
      }
      if (parsed?.status === 502) {
        setMetaErrorText(
          visibleMetaErrorText(parsed.metaError) || t('whatsapp.metaErrorUnavailable'),
        );
        return;
      }
      notify.error(tc('error'), t('whatsapp.templateCreateFailed'));
    }
  };

  const errorMessage = placeholderIndexTooHigh
    ? t('whatsapp.placeholderIndexTooHigh', { max: MAX_TEMPLATE_PLACEHOLDER_INDEX })
    : errorCode
      ? isTemplateValidationCode(errorCode)
        ? t(`whatsapp.templateError.${errorCode}`)
        : t('whatsapp.templateCreateFailed')
      : conflict
        ? t('whatsapp.templateExists')
        : null;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('whatsapp.createTemplateTitle')}
      isDirty={isDirty}
      onSubmit={() => void handleSubmit()}
      isLoading={createTemplate.isPending}
      submitDisabled={!canManageWhatsApp}
      maxWidth="sm"
    >
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {metaErrorText && (
            <Alert severity="error">
              <Typography variant="body2" gutterBottom>
                {t('whatsapp.metaRejected')}
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                }}
              >
                {metaErrorText}
              </Box>
            </Alert>
          )}
          <TextField
            label={t('whatsapp.templateName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText={t('whatsapp.templateNameHint')}
            autoComplete="off"
            fullWidth
            slotProps={{ htmlInput: { maxLength: TEMPLATE_NAME_MAX_LENGTH } }}
          />
          <TextField
            select
            label={t('whatsapp.templateLanguage')}
            value={language}
            onChange={(e) => {
              const value = e.target.value;
              if (isWhatsAppTemplateLanguage(value)) setLanguage(value);
            }}
            fullWidth
          >
            {WHATSAPP_TEMPLATE_LANGUAGES.map((code) => (
              <MenuItem key={code} value={code}>
                {LANGUAGE_ENDONYMS[code]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('whatsapp.templateCategory')}
            value={category}
            onChange={(e) => {
              const value = e.target.value;
              if (isWhatsAppTemplateCategory(value)) setCategory(value);
            }}
            fullWidth
          >
            {WHATSAPP_TEMPLATE_CATEGORIES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`whatsapp.category.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('whatsapp.templateBody')}
            value={body}
            onChange={(e) => handleBodyChange(e.target.value)}
            helperText={t('whatsapp.templateBodyHint')}
            fullWidth
            multiline
            minRows={4}
            slotProps={{ htmlInput: { maxLength: TEMPLATE_BODY_MAX_LENGTH } }}
          />
          {Array.from({ length: visibleExampleCount }, (_, index) => (
            <Stack key={index} spacing={2}>
              <TextField
                select
                label={t('whatsapp.parameterField', { index: index + 1 })}
                value={sources[index] ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setSources((prev) => {
                    const next = resizeSources(prev, visibleExampleCount);
                    next[index] = isWhatsAppTemplateParameterSource(value) ? value : null;
                    return next;
                  });
                }}
                fullWidth
              >
                <MenuItem value="">{t('whatsapp.parameterSourceFreeText')}</MenuItem>
                {WHATSAPP_TEMPLATE_PARAMETER_SOURCES.map((key) => (
                  <MenuItem key={key} value={key}>
                    {t(`whatsapp.parameterSource.${key}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t('whatsapp.templateExample', { index: index + 1 })}
                value={examples[index] ?? ''}
                onChange={(e) => {
                  setExamples((prev) => {
                    const next = resizeExamples(prev, visibleExampleCount);
                    next[index] = e.target.value;
                    return next;
                  });
                }}
                fullWidth
                autoComplete="off"
              />
            </Stack>
          ))}
          <TextField
            label={t('whatsapp.templateFooter')}
            value={footer}
            onChange={(e) => setFooter(e.target.value)}
            helperText={t('whatsapp.templateFooterHint')}
            fullWidth
            autoComplete="off"
            slotProps={{ htmlInput: { maxLength: TEMPLATE_FOOTER_MAX_LENGTH } }}
          />
          {body.trim().length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                {t('whatsapp.templatePreview')}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {renderTemplateBody(body, examples)}
              </Typography>
              {footer.trim() ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {footer}
                </Typography>
              ) : null}
            </Paper>
          )}
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
